import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useRouter } from 'src/routes/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';

import { companyApi, type CompanyDto, type UpdateCompanyDto } from 'src/api/company.api';

// ----------------------------------------------------------------------

export function CompanyEditView() {
  const router = useRouter();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState<CompanyDto | null>(null);
  const [formData, setFormData] = useState<UpdateCompanyDto>({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Partial<UpdateCompanyDto>>({});

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const companyData = await companyApi.getById(id);
        setCompany(companyData);
        setFormData({
          name: companyData.name,
          email: companyData.email,
          phone: companyData.phone,
          address: companyData.address,
          website: companyData.website || '',
          description: companyData.description || '',
          isActive: companyData.isActive,
        });
      } catch (error) {
        console.error('Failed to fetch company:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  const handleInputChange = useCallback((field: keyof UpdateCompanyDto) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [field]: event.target.value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    }, [errors]
  );

  const handleSwitchChange = useCallback((field: keyof UpdateCompanyDto) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [field]: event.target.checked }));
    }, []
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<UpdateCompanyDto> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Firma adı gereklidir';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir email adresi giriniz';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Telefon numarası gereklidir';
    }

    if (!formData.address?.trim()) {
      newErrors.address = 'Adres gereklidir';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Geçerli bir website URL\'si giriniz (http:// veya https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!validateForm() || !id) {
      return;
    }

    setSaving(true);
    try {
      await companyApi.update(id, formData);
      router.push('/dashboard/companies');
    } catch (error) {
      console.error('Failed to update company:', error);
    } finally {
      setSaving(false);
    }
  }, [formData, validateForm, id, router]);

  const handleCancel = useCallback(() => {
    router.push('/dashboard/companies');
  }, [router]);

  if (loading) {
    return (
      <DashboardContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Yükleniyor...</Typography>
        </Box>
      </DashboardContent>
    );
  }

  if (!company) {
    return (
      <DashboardContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Firma bulunamadı</Typography>
        </Box>
      </DashboardContent>
    );
  }

  return (
    <DashboardContent>
      <Box display="flex" alignItems="center" mb={5}>
        <Button
          onClick={handleCancel}
          startIcon={<Iconify icon="eva:arrow-back-fill" />}
          sx={{ mr: 2 }}
        >
          Geri
        </Button>
        <Typography variant="h4">Firma Düzenle: {company.name}</Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Firma Adı"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleInputChange('website')}
                error={!!errors.website}
                helperText={errors.website}
                placeholder="https://example.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adres"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleInputChange('address')}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Açıklama"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.isActive}
                    onChange={handleSwitchChange('isActive')}
                    color="primary"
                  />
                }
                label="Aktif"
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={handleCancel}>
              İptal
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={saving}
              startIcon={<Iconify icon="eva:save-fill" />}
            >
              Güncelle
            </LoadingButton>
          </Stack>
        </Box>
      </Card>
    </DashboardContent>
  );
} 